import { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LogoMark from "@/components/LogoMark";
import { downloadImage, printElement } from "@/lib/pdf";
import { Printer, Image as ImageIcon } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("id-ID").format(Number(n || 0));

export default function Invoice({ order, open, onClose }) {
  if (!order) return null;
  const o = order;
  const total = o.total_amount || 0;
  const dp = o.down_payment || 0;
  const isLunas = o.payment_status === "lunas";
  const sisa = Math.max(0, total - dp);
  const received = isLunas ? sisa : dp;
  const title = isLunas ? "KUITANSI PELUNASAN PESANAN AQIQAH" : "KUITANSI DP PESANAN AQIQAH";

  const childGender = o.child_gender || o.gender;
  const putra = childGender === "perempuan" ? "Putri" : "Putra";
  const sex = (o.lamb_sex || "").toLowerCase() === "betina" ? "BETINA" : "JANTAN";

  const rows = [];
  if (o.lamb_type || o.unit_price) {
    rows.push({
      label: `Domba : ${(o.lamb_type || "-").toUpperCase()} - ${sex} (DIMASAK)`,
      qty: o.lamb_count || 1, price: o.unit_price || 0, sub: (o.unit_price || 0) * (o.lamb_count || 1),
    });
  }
  if (o.rice_box_type || o.box_count) {
    rows.push({
      label: `Nasi Box : ${(o.rice_box_type || "-").toUpperCase()}`,
      qty: o.box_count || 0, price: o.box_price || 0, sub: (o.box_price || 0) * (o.box_count || 0),
    });
  }
  (o.addons || []).forEach((a) => {
    rows.push({ label: `Add On : ${a.name || "-"}`, qty: a.qty || 1, price: a.price || 0, sub: (a.price || 0) * (a.qty || 1) });
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
        <DialogTitle className="sr-only">{title} {o.order_number}</DialogTitle>
        <div className="flex items-center justify-between border-b border-brand-line px-6 py-4 no-print">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Kuitansi Pesanan</h2>
          <div className="flex gap-2">
            <Button onClick={() => downloadImage("invoice-print", `kuitansi-${o.order_number}.jpg`)} data-testid="jpg-invoice-btn"
              variant="outline" className="gap-2 border-brand-line">
              <ImageIcon className="h-4 w-4" /> JPG
            </Button>
            <Button onClick={() => printElement("invoice-print")} data-testid="print-invoice-btn"
              className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Printer className="h-4 w-4" /> Cetak
            </Button>
          </div>
        </div>

        <div id="invoice-print" className="bg-white p-8 text-brand-espresso">
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-brand-gold pb-4">
            <div className="flex items-center gap-3">
              <LogoMark size={52} textClass="text-3xl" />
              <div className="leading-tight">
                <p className="font-serif text-2xl font-bold">Rizqia Aqiqah</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-brand-espresso">Hemat, Praktis, Amanah</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-brand-espresso">No. Pesanan</p>
              <p className="font-semibold">{o.order_number}</p>
            </div>
          </div>

          {/* Title */}
          <h1 className="mt-5 text-center font-serif text-2xl font-bold uppercase tracking-wide">{title}</h1>

          {/* Body text */}
          <div className="mt-5 space-y-1 text-sm leading-relaxed">
            <p>
              Telah diterima uang sebesar <span className="font-bold">Rp {fmt(received)}</span>{" "}
              (dari total pembayaran sebesar Rp {fmt(total)}), untuk Pesanan Aqiqah atas nama :{" "}
              <span className="font-serif text-lg font-bold">{(o.child_name || o.aqiqah_name || "-")}.</span>{" "}
              {putra} dari (Ayah): <span className="font-medium">{o.father_name || "-"}</span>, dan (Ibu):{" "}
              <span className="font-medium">{o.mother_name || "-"}</span>, dengan perincian sebagai berikut :
            </p>
          </div>

          {/* Perincian */}
          <table className="mt-4 w-full border border-gray-500 text-sm">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="border border-gray-500 px-2 py-1.5 text-center w-10">No</th>
                <th className="border border-gray-500 px-2 py-1.5">Jenis Pesanan</th>
                <th className="border border-gray-500 px-2 py-1.5 text-center w-16">Qty</th>
                <th className="border border-gray-500 px-2 py-1.5 text-right w-28">Harga (Rp)</th>
                <th className="border border-gray-500 px-2 py-1.5 text-right w-28">Jumlah (Rp)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="border border-gray-500 px-2 py-1.5 text-center">{i + 1}</td>
                  <td className="border border-gray-500 px-2 py-1.5">{r.label}</td>
                  <td className="border border-gray-500 px-2 py-1.5 text-center">{r.qty}</td>
                  <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(r.price)}</td>
                  <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(r.sub)}</td>
                </tr>
              ))}
              {o.shipping_cost > 0 && (
                <tr>
                  <td className="border border-gray-500 px-2 py-1.5 text-center">{rows.length + 1}</td>
                  <td className="border border-gray-500 px-2 py-1.5" colSpan={3}>Ongkos Kirim</td>
                  <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(o.shipping_cost)}</td>
                </tr>
              )}
              {o.discount > 0 && (
                <tr>
                  <td className="border border-gray-500 px-2 py-1.5 text-right" colSpan={4}>Diskon</td>
                  <td className="border border-gray-500 px-2 py-1.5 text-right">- {fmt(o.discount)}</td>
                </tr>
              )}
              <tr className="font-semibold">
                <td className="border border-gray-500 px-2 py-1.5 text-right" colSpan={4}>TOTAL PESANAN YANG HARUS DIBAYAR</td>
                <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(total)}</td>
              </tr>
              <tr>
                <td className="border border-gray-500 px-2 py-1.5 text-right" colSpan={4}>Uang Muka yang dibayarkan</td>
                <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(dp)}</td>
              </tr>
              <tr className="font-bold text-brand-gold">
                <td className="border border-gray-500 px-2 py-1.5 text-right" colSpan={4}>SISA PEMBAYARAN</td>
                <td className="border border-gray-500 px-2 py-1.5 text-right">{fmt(sisa)}</td>
              </tr>
            </tbody>
          </table>

          {/* Catatan + signature */}
          <div className="mt-6 flex items-start justify-between gap-6">
            <div className="max-w-sm rounded-lg bg-gray-100 p-3 text-xs text-brand-espresso">
              <span className="font-semibold text-brand-espresso">Catatan: </span>
              Untuk hasil yang maksimal, masakan sebaiknya disajikan 4-5 jam setelah diterima. Kerusakan masakan di luar waktu tersebut bukan menjadi tanggung jawab kami.
            </div>
            <div className="text-center text-sm">
              <p>Bandung, {formatDate(o.created_at)}</p>
              <div className="mt-16 font-serif text-base font-bold">Yuni Ahdiyani, S.Si, M.M</div>
              <p className="text-xs text-brand-espresso">Rizqia Aqiqah</p>
            </div>
          </div>

          <p className="mt-6 border-t border-brand-line pt-3 text-center text-xs text-brand-espresso">
            Terima kasih atas kepercayaan Anda kepada Rizqia Aqiqah — Barakallahu fiikum.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
